import React, {useCallback, useEffect, useMemo} from 'react';
import {MessageDescriptor, useIntl} from 'react-intl';

import {useUI} from 'coreHooks';
import {Head} from 'coreUtils/adapters/head';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {ISeoMetaDataProps} from '../ISeoProps';

const MetaData = ({metadata}: ISeoMetaDataProps) => {
	const {managerRepository} = useGeneralProvider();
	const intl = useIntl();
	const htmlCssClasses = useUI('htmlCssClasses');
	const htmlClassesString = useMemo(() => htmlCssClasses.join(' '), [htmlCssClasses]);
	const currentLocale = managerRepository.getLocalesMgr().getCurrentLocale();
	const localeCode = currentLocale.getCode();

	useEffect(() => {
		document.documentElement.className = htmlClassesString;
	}, [htmlClassesString]);

	const getTagContent = useCallback((tagData: string | MessageDescriptor) => {
		if (tagData) {
			if (typeof tagData === 'string') {
				return tagData;
			} else {
				try {
					return intl.formatMessage(tagData);
				} catch (e) {
					console.error('Wrong metadata', tagData);

					return null;
				}
			}
		}

		return null;
	}, [intl]);

	const robotsMetaTag = useMemo(() => metadata.pageMetaTags?.find(metaTag => metaTag.ID === 'robots'), [metadata]);
	const robots = useMemo(() => robotsMetaTag ? getTagContent(robotsMetaTag.content) : null, [robotsMetaTag]);
	const title = useMemo(() => getTagContent(metadata.title), [metadata.title]);
	const description = useMemo(() => getTagContent(metadata.description), [metadata.description]);
	const keywords = useMemo(() => getTagContent(metadata.keywords), [metadata.keywords]);

	let languageAttributes;

	try {
		languageAttributes = managerRepository.getLanguagesMgr().getLanguageAttributes();
	} catch (e) {
		languageAttributes = managerRepository
			.getLanguagesMgr()
			.getLanguageAttributes(managerRepository.getLanguagesMgr().getDefaultLanguage().getLanguageCode());
	}

	return (
		<Head
			htmlAttributes={{
				lang: languageAttributes.code,
				dir: languageAttributes.direction,
			}}
		>
			{title && <title>{title}</title>}
			{robots && <meta key="pageMetaNoindex" name={'robots'} content={robots} />}
			{description && <meta key="pageMetaDescription" name={'description'} content={description} />}
			{keywords && <meta key="pageMetaKeywords" name={'keywords'} content={keywords} />}
			{metadata?.pageMetaTags?.map((metaTag, index) => {
				const content = getTagContent(metaTag.content);

				if (metaTag.name) {
					return <meta key={`pageMetaTag${index}`} name={metaTag.ID} content={content} />;
				} else if (metaTag.property) {
					return <meta key={`pageMetaTag${index}`} property={metaTag.ID} content={content} />;
				}
			})}
			<meta
				httpEquiv="Content-Security-Policy"
				content={managerRepository.getContentSecurityPolicyMgr().getMetaContent()}
			/>
			<meta httpEquiv="content-language" content={localeCode} />
		</Head>
	);
};

export default MetaData;
